import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { University, PaginatedResponse } from "../types";

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

const UNI_TYPES = [
  { value: "", label: "Barchasi" },
  { value: "Davlat", label: "Davlat" },
  { value: "Nodavlat", label: "Nodavlat" },
];

export default function UniversitiesPage() {
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const [uniType, setUniType] = useState("");
  const [page, setPage] = useState(1);
  const [regions, setRegions] = useState<string[]>([]);
  const [data, setData] = useState<PaginatedResponse<University> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/filters/regions").then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(
    (p: number) => {
      setLoading(true);
      const params: Record<string, string | number> = { page: p, size: 20 };
      if (search.trim()) params.search = search.trim();
      if (region) params.region = region;
      if (uniType) params.uni_type = uniType;

      api
        .get("/universities", { params })
        .then((r) => {
          setData(r.data);
          setPage(p);
        })
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    },
    [search, region, uniType]
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData(1);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Universitetlar</h1>
        <p className="text-slate-500">O'zbekiston oliy ta'lim muassasalari</p>
      </div>

      {/* Search and filters */}
      <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search input */}
          <div className="flex-1 relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Universitet nomini qidiring..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
            />
          </div>

          {/* Filter chips */}
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-[160px]"
            >
              <option value="">Barcha hududlar</option>
              {regions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={uniType}
              onChange={(e) => setUniType(e.target.value)}
              className="border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-[130px]"
            >
              {UNI_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filters */}
        {(region || uniType) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
            {region && (
              <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-lg px-3 py-1.5 text-xs font-medium">
                {region}
                <button onClick={() => setRegion("")} className="ml-0.5 hover:text-indigo-900">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            {uniType && (
              <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-lg px-3 py-1.5 text-xs font-medium">
                {uniType}
                <button onClick={() => setUniType("")} className="ml-0.5 hover:text-indigo-900">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            <button
              onClick={() => { setRegion(""); setUniType(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              Barchasini tozalash
            </button>
          </div>
        )}
      </form>

      {/* Results */}
      {loading ? (
        <Spinner />
      ) : data && data.items.length > 0 ? (
        <>
          <p className="text-sm text-slate-500 mb-5">
            Jami <span className="font-bold text-slate-800">{data.total}</span> ta universitet
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.items.map((uni) => (
              <Link
                key={uni.edu_id}
                to={`/universities/${uni.edu_id}`}
                className="group bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md hover:border-slate-200 transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-4">
                  {uni.logo_url ? (
                    <img
                      src={uni.logo_url}
                      alt={uni.name}
                      className="w-16 h-16 rounded-xl object-contain flex-shrink-0 bg-slate-50 border border-slate-100"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center flex-shrink-0 border border-indigo-100/50">
                      <span className="text-indigo-600 font-bold text-xl">
                        {uni.name?.charAt(0) || "U"}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2 text-sm leading-snug">
                      {uni.name}
                    </h3>
                    {uni.region_name && (
                      <p className="flex items-center gap-1 text-xs text-slate-500 mt-1.5">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {uni.region_name}
                      </p>
                    )}
                    {uni.uni_type && (
                      <span className={`inline-block mt-2 text-xs font-medium px-2.5 py-1 rounded-lg ${
                        uni.uni_type === "Davlat"
                          ? "bg-indigo-50 text-indigo-700"
                          : "bg-purple-50 text-purple-700"
                      }`}>
                        {uni.uni_type}
                      </span>
                    )}
                  </div>
                  <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-400 transition-colors flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-10">
              <button
                disabled={page <= 1}
                onClick={() => fetchData(page - 1)}
                className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Oldingi
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(data.pages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (data.pages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= data.pages - 2) {
                    pageNum = data.pages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchData(pageNum)}
                      className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${
                        page === pageNum
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                disabled={page >= data.pages}
                onClick={() => fetchData(page + 1)}
                className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
              >
                Keyingi
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <p className="text-slate-700 text-lg font-semibold mb-1">Universitet topilmadi</p>
          <p className="text-slate-400 text-sm">Qidiruv so'zini o'zgartirib ko'ring</p>
        </div>
      )}
    </div>
  );
}
